import React from 'react';

/**
 * The three voices.
 *
 *   POSTER  — the number, the headline.   A film title card.
 *   STORY   — the meaning, the romance.   A letter you kept.
 *   MONO    — the evidence, the metadata. A case file.
 *
 * Every composed surface uses at least two of the three. One voice alone is
 * off-brand.
 *
 * CONTRAST RULE, enforced here rather than per-callsite so it cannot regress:
 * --cp-text-3 (#6E6B94) measures 3.90:1 on canvas, 4.05:1 on void and 3.59:1
 * on surface — all under AA 4.5:1, and "small mono" is not a WCAG large-text
 * category. It is therefore DECORATIVE ONLY. Every component below renders
 * visible text in --cp-text-2 (#9A97C0 — 7.03:1 canvas / 7.31:1 void).
 */

/* ------------------------------------------------------------------ MONO */

/**
 * MonoKicker — "TWO STRANGERS · ONE MAP".
 * Always uppercase, always wide-tracked. Separator is always the interpunct;
 * never a hyphen, never a pipe.
 */
export function MonoKicker({ children, className = '', tone = 'secondary', as: Tag = 'p' }) {
  const tones = {
    secondary: 'text-ink-2',
    gold: 'text-gold-400',
    paper: 'text-paper',
  };
  return (
    <Tag
      className={`cp-mono text-[length:var(--fs-mono)] ${tones[tone] || tones.secondary} ${className}`}
    >
      {children}
    </Tag>
  );
}

/** The 24×2 gold rule that sits 8px above a kicker. */
export function GoldRule({ className = '', width = 24, height = 2, animated = false }) {
  return (
    <span
      aria-hidden="true"
      className={`block bg-gold-400 ${animated ? 'arrive-hairline' : ''} ${className}`}
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
}

/* ---------------------------------------------------------------- POSTER */

/**
 * PosterHeadline — the display voice, split filled / gold-outline.
 *
 *   <PosterHeadline lines={[{ text: '460 times' }, { text: 'before hello', outline: true }]} />
 *
 * Real text in the DOM, never an image; the outline is CSS stroke, not SVG.
 * Both lines live inside one heading so the accessible name reads as one
 * phrase. Never more than ~6 words, never more than two lines.
 */
export function PosterHeadline({
  lines = [],
  size = 'hero',
  className = '',
  as: Tag = 'h2',
  id,
  arrive = false,
  glow = true,
}) {
  const sizes = {
    hero: 'text-[length:var(--fs-poster-hero)]',
    section: 'text-[length:var(--fs-poster-h2)]',
  };
  const delays = ['d-200', 'd-300'];

  return (
    <Tag id={id} className={`cp-poster ${sizes[size]} ${className}`}>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {/* Block spans concatenate with no whitespace, so the accessible
              name would read "460 timesbefore hello". This restores the word
              break for assistive tech without affecting layout. */}
          {i > 0 && <span className="sr-only"> </span>}
          <span
            className={`block ${line.outline ? 'cp-outline' : ''} ${
              arrive ? `arrive ${delays[i] || 'd-300'}` : ''
            } ${line.className || ''}`}
            style={
              !line.outline && glow
                ? { textShadow: '0 0 40px rgba(247, 242, 233, 0.16)' }
                : undefined
            }
          >
            {line.text}
          </span>
        </React.Fragment>
      ))}
    </Tag>
  );
}

/**
 * PosterNumeral — one number, dominating. The number is the hero.
 * Allowed to break the grid; the glow is permitted here and nowhere else.
 */
export function PosterNumeral({ children, className = '', tone = 'gold', hang = false }) {
  const tones = { gold: 'text-gold-400', paper: 'text-paper' };
  return (
    <span
      className={`cp-poster cp-numeral-glow block text-[length:var(--fs-numeral)] ${tones[tone]} ${className}`}
      style={hang ? { marginLeft: '-0.06em' } : undefined}
    >
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------- STORY */

/**
 * SerifStory — one or two sentences, max. The emotional line under the
 * poster headline. The signature move is GoldClause on the final clause.
 */
export function SerifStory({ children, className = '', as: Tag = 'p', size = 'md' }) {
  const sizes = {
    md: 'text-[length:var(--fs-serif)]',
    body: 'text-[length:var(--fs-body)]',
  };
  return <Tag className={`cp-story ${sizes[size]} ${className}`}>{children}</Tag>;
}

/**
 * GoldClause — the final clause, tinted Signal Gold.
 * Real selectable, translatable text; never baked into an image.
 */
export function GoldClause({ children, className = '' }) {
  return <em className={`italic text-gold-400 ${className}`}>{children}</em>;
}

/* -------------------------------------------------------------- DATA ROW */

/**
 * DataRow — LABEL ················· VALUE over a paper hairline.
 *
 * The case-file texture that recurs in every section as structural rhythm.
 * At ≤420px the dot leader collapses and the row wraps label-above-value:
 * a value must never truncate.
 */
export function DataRow({ label, value, valueTone = 'paper', className = '', animated = false }) {
  const tones = {
    paper: 'text-paper',
    gold: 'text-gold-400',
    coral: 'text-coral',
  };
  return (
    <div
      className={`w-full border-b border-[color:var(--cp-hairline)] pb-[10px] ${
        animated ? 'arrive-hairline' : ''
      } ${className}`}
    >
      <div className="flex flex-col items-start gap-1 min-[421px]:flex-row min-[421px]:items-baseline min-[421px]:gap-0">
        <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">{label}</span>
        <span
          aria-hidden="true"
          className="hidden min-w-[24px] flex-1 -translate-y-[0.28em] mx-3 border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
        />
        <span
          className={`cp-mono text-[length:var(--fs-mono-value)] ${tones[valueTone]} min-[421px]:whitespace-nowrap`}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ IDENTITY */

/**
 * IdentityLegend — GOLD = YOU · CORAL = THEM.
 *
 * Colourblind law: the encoding must survive grayscale, so every signal
 * carries a glyph AND a word in the DOM as text, not colour alone. Marks
 * are shape-differentiated too — ● self, ◆ them.
 *
 * Mount this wherever gold/coral carry identity meaning. Never swap the
 * mapping: gold is never the partner, coral is never the self.
 */
export function IdentityLegend({ className = '', includeCrossing = false }) {
  const items = [
    { glyph: '●', name: 'GOLD', word: 'YOU', color: 'text-gold-400' },
    { glyph: '◆', name: 'CORAL', word: 'THEM', color: 'text-coral' },
    ...(includeCrossing
      ? [{ glyph: '✦', name: 'PAPER', word: 'THE CROSSING', color: 'text-paper' }]
      : []),
  ];

  return (
    <ul className={`flex flex-wrap items-center gap-x-5 gap-y-2 ${className}`}>
      {items.map((item) => (
        <li key={item.word} className="flex items-center gap-2">
          <span aria-hidden="true" className={`${item.color} text-[13px] leading-none`}>
            {item.glyph}
          </span>
          <span className="cp-mono text-[length:var(--fs-mono)]">
            <span className={item.color}>{item.name}</span> = {item.word}
          </span>
        </li>
      ))}
    </ul>
  );
}
